import { initMagicBall } from '../magic-ball/magic-ball.js';

// ─── Config ───────────────────────────────────────────────────────────────────

const selectors = {
  root: '[data-sandbox]',
  ball: '[data-magic-ball]',
  control: '[data-sandbox-control]',
  state: '[data-sandbox-state]',
  output: (name) => `[data-sandbox-output="${name}"]`,
};

const properties = {
  intensity: '--magic-ball-intensity',
  count: '--magic-ball-particle-count',
  sparsity: '--magic-ball-sparsity',
};

const formats = {
  sparsity: (value) => `${Math.round(value * 100)}%`,
};

// ─── Sandbox ──────────────────────────────────────────────────────────────────

/**
 * Dev-only panel for tuning the magic ball. It only writes attributes
 * and custom properties on the ball — the block itself follows them.
 */

function initSandbox(root) {
  const ball = root.querySelector(selectors.ball);

  if (!ball) return;

  const instance = initMagicBall(ball);

  function setOutput(name, text) {
    root.querySelector(selectors.output(name)).textContent = text;
  }

  function setProperty(name, value) {
    ball.style.setProperty(properties[name], value);
    setOutput(name, formats[name]?.(value) ?? value);
  }

  /** Particles shown in the resting ring: the rest only light up while loading. */

  function updateResting() {
    const style = getComputedStyle(ball);
    const count = Number(style.getPropertyValue(properties.count));
    const sparsity = Number(style.getPropertyValue(properties.sparsity));

    setOutput('resting', `${Math.round(count * (1 - sparsity))} visible at rest`);
  }

  const controls = {
    intensity: (value) => setProperty('intensity', value),
    count: (value) => {
      setProperty('count', value);
      updateResting();
    },
    sparsity: (value) => {
      setProperty('sparsity', value);
      updateResting();
    },
  };

  /** Where the range's thumb sits, 0–1: the filled line runs up to it, the dotted rest is inactive. */

  function setProgress(control) {
    if (control.type !== 'range') return;

    const min = Number(control.min);
    const progress = (Number(control.value) - min) / (Number(control.max) - min);

    control.style.setProperty('--sandbox-range-progress', progress);
  }

  function handleInput(event) {
    const control = event.target.closest(selectors.control);

    if (!control) return;

    setProgress(control);
    controls[control.dataset.sandboxControl]?.(control.value);
  }

  function handleClick(event) {
    const trigger = event.target.closest(selectors.state);

    if (!trigger) return;

    instance.show(trigger.dataset.sandboxState);
  }

  root.querySelectorAll(selectors.control).forEach(setProgress);
  root.addEventListener('input', handleInput);
  root.addEventListener('click', handleClick);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(selectors.root).forEach(initSandbox);
});
